import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from './CTAButton';
import { Battery, Shield, TrendingUp, LifeBuoy, Users, Check } from 'lucide-react';
const blocks = [{
  number: '01',
  title: 'The 3-Block Day Map™',
  subtitle: 'Your day. Structured.',
  lines: ['15 min Reset', '5 min Admin', '45 min Relief'],
  close: 'When Reset + Relief happen —\nyou win.',
  icon: Battery,
  color: 'terracotta'
}, {
  number: '02',
  title: 'One Protected Window',
  subtitle: '30 minutes.\nNon-negotiable.',
  lines: ['Messy naps allowed.', 'Your window comes first.'],
  close: null,
  icon: Shield,
  color: 'sage'
}, {
  number: '03',
  title: 'The Gentle Ladder',
  subtitle: 'Gradual crib exposure.\nNo cry-it-out.\nNo panic.',
  lines: ['Small steps.', 'Real progress.'],
  close: null,
  icon: TrendingUp,
  color: 'terracotta'
}, {
  number: '04',
  title: 'The 20-Minute Rescue',
  subtitle: 'When the spiral hits.',
  lines: ['Stabilize.', 'Reset.', 'Continue.'],
  close: null,
  icon: LifeBuoy,
  color: 'sage'
}, {
  number: '05',
  title: 'The Partner Page',
  subtitle: 'One page.\n90 seconds to read.\nClear instructions.',
  lines: ['Support without explaining twice.'],
  close: null,
  icon: Users,
  color: 'terracotta'
}];
export function EditorialTips() {
  return <section id="system" className="relative pt-12 pb-8 md:pt-20 md:pb-12 px-6 md:px-8 bg-cream-dark overflow-hidden" data-id="element-120">
      {/* Subtle ambient glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-terracotta/5 rounded-full blur-[120px] pointer-events-none" data-id="element-121" />
      <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-sage/5 rounded-full blur-[120px] pointer-events-none" data-id="element-122" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-brown/5 to-transparent" data-id="element-123" />

      <div className="max-w-2xl mx-auto relative z-10" data-id="element-124">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="text-center mb-12 md:mb-14" data-id="element-125">
          {/* Ornamental divider */}
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-126">
            <div className="w-8 md:w-12 h-px bg-gradient-to-r from-transparent to-terracotta/30" data-id="element-127" />
            <div className="w-1.5 h-1.5 rounded-full bg-terracotta/40" data-id="element-128" />
            <div className="w-8 md:w-12 h-px bg-gradient-to-l from-transparent to-terracotta/30" data-id="element-129" />
          </div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-terracotta font-bold mb-5" data-id="element-130">
            What You Actually Get
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-brown leading-tight mb-4" data-id="element-131">
            Not theory.
          </h2>
          <p className="font-serif text-2xl md:text-3xl text-brown/55 italic leading-snug" data-id="element-132">
            A structured survival system.
          </p>
        </motion.div>

        {/* Blocks with connecting thread */}
        <div className="relative mb-12" data-id="element-133">
          {/* Vertical connecting line */}
          <div className="absolute left-6 md:left-8 top-12 bottom-12 w-px bg-gradient-to-b from-terracotta/10 via-sage/10 to-terracotta/10 pointer-events-none hidden md:block" data-id="element-134" />

          <div className="space-y-6 md:space-y-8" data-id="element-135">
            {blocks.map((block, i) => {
            const Icon = block.icon;
            const isTerracotta = block.color === 'terracotta';
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.8,
              delay: i * 0.1,
              ease: [0.25, 0.1, 0.25, 1]
            }} whileHover={{
              y: -2,
              transition: {
                duration: 0.3
              }
            }} className="group relative bg-white rounded-2xl md:rounded-3xl border border-brown/[0.06] overflow-hidden transition-shadow duration-500" style={{
              boxShadow: '0 1px 3px rgba(61,50,41,0.04), 0 8px 32px rgba(61,50,41,0.04), 0 24px 48px rgba(61,50,41,0.02)'
            }} data-id="element-136">
                  {/* Top accent gradient strip */}
                  <div className="h-[3px] w-full" style={{
                background: isTerracotta ? 'linear-gradient(90deg, transparent 10%, rgba(198,114,76,0.25) 50%, transparent 90%)' : 'linear-gradient(90deg, transparent 10%, rgba(139,152,119,0.25) 50%, transparent 90%)'
              }} data-id="element-137" />

                  <div className="p-6 md:p-8 lg:p-10" data-id="element-138">
                    {/* Top header row: Icon + Number badge */}
                    <div className="flex justify-between items-start mb-6" data-id="element-139">
                      <div className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center shrink-0 relative ${isTerracotta ? 'bg-terracotta/8 text-terracotta' : 'bg-sage/8 text-sage-dark'}`} style={{
                    boxShadow: isTerracotta ? 'inset 0 1px 4px rgba(198,114,76,0.08), 0 2px 8px rgba(198,114,76,0.06)' : 'inset 0 1px 4px rgba(139,152,119,0.08), 0 2px 8px rgba(139,152,119,0.06)'
                  }} data-id="element-140">
                        <Icon className="w-6 h-6 md:w-7 md:h-7" strokeWidth={1.8} data-id="element-141" />
                      </div>
                      {/* Editorial number */}
                      <span className="font-serif text-3xl md:text-4xl font-light text-brown/10 leading-none select-none" data-id="element-142">
                        {block.number}
                      </span>
                    </div>

                    {/* Content */}
                    <div data-id="element-143">
                      <h3 className="font-serif text-2xl md:text-3xl text-brown font-medium mb-3 leading-tight" data-id="element-144">
                        {block.title}
                      </h3>

                      {/* Subtitle — multiline */}
                      <div className="mb-6" data-id="element-145">
                        {block.subtitle.split('\n').map((line, j) => <p key={j} className="text-brown-light text-base md:text-lg leading-relaxed" data-id="element-146">
                            {line}
                          </p>)}
                      </div>

                      {/* Lines with refined Check icons */}
                      <div className="space-y-3 mb-4" data-id="element-147">
                        {block.lines.map((line, j) => <div key={j} className="flex items-start gap-3" data-id="element-148">
                            <div className="w-6 h-6 rounded-full bg-sage/8 flex items-center justify-center shrink-0 mt-0.5" data-id="element-149">
                              <Check className="w-3.5 h-3.5 text-sage-dark" strokeWidth={2.5} data-id="element-150" />
                            </div>
                            <p className="text-brown text-base font-medium leading-snug" data-id="element-151">
                              {line}
                            </p>
                          </div>)}
                      </div>

                      {/* Close quote with decorative mark */}
                      {block.close && <div className="mt-6 pt-6 border-t border-brown/[0.04]" data-id="element-152">
                          <div className="relative pl-5 border-l-2 border-terracotta/25" data-id="element-153">
                            <span className="absolute -left-1 -top-2 font-serif text-4xl text-terracotta/15 leading-none select-none" data-id="element-154">
                              "
                            </span>
                            {block.close.split('\n').map((line, j) => <p key={j} className="font-serif italic text-brown/65 text-lg leading-relaxed" data-id="element-155">
                                {line}
                              </p>)}
                          </div>
                        </div>}
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Closing micro line */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="text-center mb-6" data-id="element-156">
          <div className="flex items-center justify-center gap-3 mb-5" data-id="element-157">
            <div className="w-8 h-px bg-terracotta/20" data-id="element-158" />
            <div className="w-1 h-1 rounded-full bg-terracotta/30" data-id="element-159" />
            <div className="w-8 h-px bg-terracotta/20" data-id="element-160" />
          </div>
          <p className="font-serif text-xl md:text-2xl text-brown/55 italic tracking-wide" data-id="element-161">
            Simple. Structured. Doable.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.1,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="text-center" data-id="element-162">
          <p className="font-serif text-lg md:text-xl text-brown/70 italic mb-5 leading-relaxed" data-id="element-163">
            Everything you need to reclaim your day — in one calm PDF.
          </p>

          <CTAButton text="I'm Ready for Relief" className="min-w-[300px]" data-id="element-164" />

          <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-xs text-brown-light/50" data-id="element-165">
            <span className="flex items-center gap-1.5" data-id="element-166">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-167">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-168" />
                <polyline points="7 10 12 15 17 10" data-id="element-169" />
                <line x1="12" y1="15" x2="12.01" y2="18" data-id="element-170" />
              </svg>
              Instant PDF
            </span>
            <span className="text-brown-light/20" data-id="element-171">·</span>
            <span className="flex items-center gap-1.5" data-id="element-172">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-173">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" data-id="element-174" />
                <line x1="12" y1="18" x2="12.01" y2="18" data-id="element-175" />
              </svg>
              Phone-friendly
            </span>
            <span className="text-brown-light/20" data-id="element-176">·</span>
            <span className="flex items-center gap-1.5" data-id="element-177">
              <svg className="w-3.5 h-3.5 text-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-178">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-179" />
                <polyline points="9 12 11 14 15 10" data-id="element-180" />
              </svg>
              14-day guarantee
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}